import {
  openModal,
  routeTo,
  routeBack,
  finish,
  partiallyUpdate
} from '../../../core/actions';
import FlowHeader from '../../../components/FlowHeader/FlowHeader';
import FooterSite from '../../../components/FooterSite/FooterSite';
import BaseLayout from '../../../components/BaseLayout/BaseLayout';
import SummitRewards from '../../../components/BaseLayout/SummitRewards';
import {
  hasURLTickers,
  hasExistingFunds,
  isValidEmail,
  isValidSSN,
  isValidMaskedSSN,
  passwordValidation,
  isValidDob,
  meetsTrustTaxableMinimumInvestment,
  chose,
  chosenAddressValidation,
  isReviewMode,
  existingUserShouldGotoLogin,
  existingUserShouldGotoEnablement,
  isUsernameAlreadyTaken,
  choseResultingPath,
  userValidationFailed,
  fundingFromExistingAccount,
  compose,
  fundingFromCheck,
  isUniqueSSN,
  isAuthenticated,
  trustUsernameValidation,
  guestUserLoginRequired,
  showUpdateToIClassModal
} from '../../shared/testers';

import TaxableProfileScreen from '../../../screens/ProfileScreen/TaxableProfileScreen';
import TrustInformationScreen from '../../../screens/TrustInformationScreen/TrustInformationScreen';
import CreateCredentialsScreen from '../../../screens/CreateCredentialsScreen/CreateCredentialsScreen';
import TrusteeContactInformationScreen from '../../../screens/TrusteeContactInformationScreen/TrusteeContactInformationScreen';
import TrusteeInformationScreen from '../../../screens/TrusteeInformationScreen/TrusteeInformationScreen';
import ReviewScreen from '../../../screens/ReviewScreen/ReviewScreen';
import ConfirmationScreen from '../../../screens/ConfirmationScreen/ConfirmationScreen';
import InvestmentOptionsScreen from '../../../screens/InvestmentOptionsScreen/InvestmentOptionsScreen';
import InvestmentReviewScreen from '../../../screens/InvestmentReviewScreen/InvestmentReviewScreen';
import BYOPScreen from '../../../screens/BYOPScreen/BYOPScreen';
import MoneyMarketScreen from '../../../screens/MoneyMarketScreen/MoneyMarketScreen';
import TrustFundingScreen from '../../../screens/TrustFundingScreen/TrustFundingScreen';
import TrustExistingAgreementsScreen from '../../../screens/TrustExistingAgreementsScreen/TrustExistingAgreementsScreen';
import TrustAgreementAllocationsScreen from '../../../screens/TrustAgreementAllocationsScreen/TrustAgreementAllocationsScreen';
import TrustChecklistScreen from '../../../screens/ChecklistScreen/TrustChecklistScreen';
import InvestmentOptionsContents from '../../../components/InvestmentOptions/TaxableContents';
import TrustFundingOptions from '../../../components/TrustFundingContent/TrustFundingOptions';
import UserValidationModal from '../../../components/UserValidationModal/UserValidationModal';
import makeForcedLoginModal from '../../../components/ForcedLoginModal/ForcedLoginModal';
import GuestUserLoginModal from '../../../components/GuestUserLoginModal/GuestUserLoginModal';
import AddressValidationModal from '../../../components/AddressValidationModal/AddressValidationModal';
import CredentialsValidationModal from '../../../components/CredentialsValidationModal/CredentialsValidationModal';
import UpdateToIClassModal from '../../../components/UpdateToIClass/UpdateToIClassModal';

import {
  commitProfileData,
  commitTrusteeInformation,
  commitReferrerForm,
  registerAndLoginUser,
  makeLoadProfileData,
  commitTrustInformation,
  commitTrusteeContactInformation,
  commitTrustFundingMethodType,
  commitExistingInvestments,
  updateAnalytics,
  createTrustApplication,
  loadTrustSubmissionForm,
  loadExistingAgreements
} from './loaders';
import { commitNewInvestmentsBYOP, loadLoginData } from '../shared/loaders';
import {
  loadTermsForSubmission,
  commitShoppingFunds,
  loadMoneyMarketData,
  commitMoneyMarketFund
} from '../../shared/loaders';
import { FlowNode } from '../../../core/types/flow';
import {
  accountTypes,
  contactTextFn,
  fundingTypes,
  redirectToCLX,
  redirectToEnablement
} from '../../shared/utils';
import { registerFlow } from '../../../core/flow-register';
import { updateTrustJourneyProfile } from '../../return-journey/loaders';

export const nodes: Array<FlowNode> = [
  // profile data will be inherited by the trustee (primary customer)
  {
    route: 'landing',
    screen: TrustChecklistScreen,
    load: loadLoginData,
    commit: commitShoppingFunds,
    next: {
      default: routeTo('profile')
    },
    flowProps: {
      displayStickyBanner: true,
      getContactText: contactTextFn(),
      accountType: accountTypes.TRUST,
      analytics: {
        pageName: 'account-info'
      }
    }
  },
  {
    route: 'profile',
    screen: TaxableProfileScreen,
    load: makeLoadProfileData(),
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'primaryEmailAddress',
        required: true,
        validation: isValidEmail
      },
      {
        name: 'amount',
        required: true,
        validation: meetsTrustTaxableMinimumInvestment
      }
    ],
    commit: commitProfileData,
    next: {
      cases: [
        [
          compose(chose('skipLogin'), hasExistingFunds),
          routeTo('funding/options')
        ],
        [chose('skipLogin'), routeTo('trustee/information')],
        [guestUserLoginRequired, openModal(GuestUserLoginModal)],
        [hasExistingFunds, routeTo('funding/options')]
      ],
      default: routeTo('trustee/information')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      title: 'How will you fund this account?',
      accountType: accountTypes.TRUST,
      analytics: {
        pageName: 'About'
      }
    }
  },
  {
    route: 'funding/options',
    screen: TrustFundingScreen,
    commit: commitTrustFundingMethodType,
    load: loadExistingAgreements,
    next: {
      cases: [
        [
          chose(fundingTypes.FROM_EXISTING_ACCOUNT),
          routeTo('funding/existing-accounts')
        ],
        [
          compose(chose(fundingTypes.CHECK), hasExistingFunds),
          routeTo('trustee/information')
        ]
      ],
      default: routeTo('review')
    },
    flowProps: {
      title: "You'll need to fund your trust via check",
      getContactText: contactTextFn('PICI'),
      options: TrustFundingOptions,
      analytics: {
        pageName: 'TrustFundingOptions'
      }
    }
  },
  {
    route: 'funding/existing-accounts',
    screen: TrustExistingAgreementsScreen,
    commit: commitExistingInvestments,
    next: {
      cases: [
        [
          choseResultingPath('SELECTIVE'),
          routeTo('funding/existing-accounts/allocation')
        ],
        [isReviewMode(), routeTo('review')]
      ],
      default: routeTo('trustee/information')
    },
    flowProps: {
      title: 'Select source of funds',
      getContactText: contactTextFn('Funding'),
      analytics: {
        pageName: 'ExistingAccounts'
      }
    }
  },
  {
    route: 'funding/existing-accounts/allocation',
    screen: TrustAgreementAllocationsScreen,
    commit: commitExistingInvestments,
    next: {
      cases: [[isReviewMode(), routeTo('review')]],
      default: routeTo('trustee/information')
    },
    flowProps: {
      title: 'Edit your selection',
      getContactText: contactTextFn('Funding'),
      analytics: {
        pageName: 'ExistingAccountAllocation'
      }
    }
  },
  {
    route: 'investment',
    screen: InvestmentOptionsScreen,
    next: {
      cases: [[chose('money-market'), routeTo('money-market')]],
      default: routeTo('byop')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBasicPI'),
      layoutClass: 'wide',
      title: 'How would you like to choose funds for your trust account?',
      options: InvestmentOptionsContents,
      analytics: {
        pageName: 'TaxableInvestmentOptions'
      }
    }
  },
  {
    route: 'money-market',
    screen: MoneyMarketScreen,
    load: loadMoneyMarketData,
    commit: commitMoneyMarketFund,
    next: {
      cases: [[chose('viewAllMoneyMarketFunds'), routeTo('byop')]],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'MoneyMarketFund'
      }
    }
  },
  {
    route: 'byop',
    screen: BYOPScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [choseResultingPath('updateAndBack'), routeBack()]
      ],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'BuildPortfolio'
      }
    }
  },
  {
    route: 'byop/review',
    screen: InvestmentReviewScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [choseResultingPath('updateAndBack'), routeBack()],
        [choseResultingPath('reselect-funds'), routeTo('byop')],
        [hasExistingFunds, routeTo('review')],
        [isReviewMode(), routeTo('review')]
      ],
      default: routeTo('funding/options')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'wide',
      analytics: {
        pageName: 'BuildPortfolioSummary'
      }
    }
  },
  {
    route: 'trustee/information',
    screen: TrusteeInformationScreen,
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'middleName'
      },
      {
        name: 'dob',
        required: true,
        validation: isValidDob
      },
      { name: 'citizenship', required: true },
      {
        name: 'ssn',
        required: true,
        validation: [isValidMaskedSSN]
      },
      {
        name: 'coTrusteeFirstName',
        required: true
      },
      {
        name: 'coTrusteeLastName',
        required: true
      },
      {
        name: 'coTrusteeMiddleName'
      },
      {
        name: 'coTrusteeDob',
        required: true,
        validation: isValidDob
      },
      { name: 'coTrusteeCitizenship', required: true },
      {
        name: 'coTrusteeSsn',
        required: true,
        validation: [isValidSSN, isUniqueSSN]
      }
    ],
    startsPersistingSensitive: true,
    commit: commitTrusteeInformation,
    next: {
      cases: [
        [userValidationFailed, openModal(UserValidationModal)],
        [
          existingUserShouldGotoEnablement,
          openModal(makeForcedLoginModal(redirectToEnablement))
        ],
        [
          existingUserShouldGotoLogin,
          openModal(makeForcedLoginModal(redirectToCLX))
        ],
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)]
      ],
      default: routeTo('contact/information')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      title: 'Enter Trustee Information',
      analytics: {
        pageName: 'TrusteeInformation'
      }
    }
  },
  {
    route: 'contact/information',
    screen: TrusteeContactInformationScreen,
    form: [
      { name: 'residentialStreet1', required: true },
      { name: 'residentialStreet2' },
      { name: 'residentialZip', required: true },
      { name: 'residentialCity', required: true },
      { name: 'residentialState', required: true },
      { name: 'mailingStreet1', required: true },
      { name: 'mailingStreet2' },
      { name: 'mailingZip', required: true },
      { name: 'mailingCity', required: true },
      { name: 'mailingState', required: true },
      { name: 'primaryEmailAddress', required: true },
      { name: 'phone1', required: true },
      { name: 'phone2' },
      { name: 'phone3' },
      { name: 'mailingAddressDifferent', type: 'checkbox' },
      { name: 'useResidentialAddress', required: true },
      { name: 'useMailingAddress', required: true }
    ],
    commit: commitTrusteeContactInformation,
    next: {
      cases: [
        [chosenAddressValidation, openModal(AddressValidationModal)],
        [isReviewMode(), routeTo('review')]
      ],
      default: routeTo('information')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      analytics: {
        pageName: 'TrusteeContactInformation'
      }
    }
  },
  {
    route: 'information',
    screen: TrustInformationScreen,
    form: [
      { name: 'trustName', required: true },
      { name: 'trustStreet1', required: true },
      { name: 'trustStreet2' },
      { name: 'trustZip', required: true },
      { name: 'trustCity', required: true },
      { name: 'trustState', required: true },
      { name: 'trustDate', required: true },
      { name: 'trustID', required: true },
      { name: 'trustIDType', required: true }
    ],
    load: loadTermsForSubmission,
    commit: commitTrustInformation,
    next: {
      cases: [
        [isReviewMode(), routeTo('review')],
        [fundingFromExistingAccount, routeTo('review')],
        [compose(hasURLTickers, isAuthenticated()), routeTo('byop/review')],
        [isAuthenticated(), routeTo('investment')],
        [compose(hasExistingFunds, fundingFromCheck), routeTo('byop')]
      ],
      default: routeTo('credentials')
    },
    flowProps: {
      title: 'Enter your Trust Information',
      getContactText: contactTextFn('PICI'),
      analytics: {
        pageName: 'TrustInformation'
      }
    }
  },
  {
    route: 'credentials',
    form: [
      {
        name: 'username',
        required: true,
        validation: trustUsernameValidation
      },
      {
        name: 'password',
        required: true,
        validation: passwordValidation
      }
    ],
    screen: CreateCredentialsScreen,
    commit: registerAndLoginUser,
    next: {
      cases: [
        [isUsernameAlreadyTaken, openModal(CredentialsValidationModal)],
        [hasURLTickers, routeTo('byop/review')]
      ],
      default: routeTo('investment')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      analytics: {
        pageName: 'Enablement'
      }
    }
  },
  {
    route: 'review',
    screen: ReviewScreen,
    load: loadTermsForSubmission,
    commit: createTrustApplication,
    next: {
      cases: [
        [chose('updateCustomer'), partiallyUpdate()],
        [chose('existing-account'), routeTo('funding/existing-accounts')],
        [chose('byop'), routeTo('byop')],
        [chose('trust'), routeTo('information')],
        [chose('trustee'), routeTo('trustee/information')]
      ],
      default: routeTo('confirmation')
    },
    form: [
      {
        name: 'eligibleForTaxWithholding'
      }
    ],
    flowProps: {
      renderTaxpayerCertifications: true,
      getContactText: contactTextFn('Review'),
      analytics: {
        pageName: 'ReviewConfirm'
      }
    }
  },
  {
    route: 'confirmation',
    screen: ConfirmationScreen,
    load: loadTrustSubmissionForm,
    commit: commitReferrerForm,
    next: {
      cases: [[chose('referrer'), partiallyUpdate()]],
      default: finish()
    },
    flowProps: {
      analytics: {
        pageName: 'Confirmation',
        isComplete: true
      }
    }
  }
];

export default registerFlow({
  namespace: `mutual-fund/general-investing/trust`,
  nodes,
  start: 'landing',
  pageDecorators: [BaseLayout, SummitRewards],
  onLoad: updateAnalytics,
  onCommit: updateTrustJourneyProfile,
  flowProps: {
    getContactText: contactTextFn(),
    header: FlowHeader,
    footer: FooterSite
  }
});
